import * as v from 'valibot';

/**
 * Warm execution environments — the pool a container-backed step is handed
 * instead of building one.
 *
 * The cost of an agent that writes code is not the model. It is the ninety
 * seconds at the start of every run spent making a machine that can compile
 * anything: a clone, a dependency install, a code index, a toolchain. Ninety
 * seconds is also, roughly, the point at which a person watching stops watching
 * — so the fix is not to make the build faster but to have already done it.
 *
 * A pool holds environments for a `(repo, ref)` that are already cloned,
 * already installed and already indexed. A step leases one, works in it, and
 * hands it back to be retired; the pool refills in the background against the
 * ref's current head. The pool is *per ref* because an environment warmed
 * against a commit nobody is on is worse than a cold start: it compiles, it
 * runs, and it answers questions about code that no longer exists.
 */

/**
 * Where an environment is in its life.
 *
 * `retired` rather than deleting the row. A leased environment that misbehaved
 * is the thing you want to look at afterwards, and a pool whose failures vanish
 * is one nobody can debug — the row is small and the reason is the whole value.
 */
export const EnvironmentState = v.picklist(['warming', 'ready', 'leased', 'retired']);
export type EnvironmentState = v.InferOutput<typeof EnvironmentState>;

// ------------------------------------------------------- lifecycle and images

/**
 * How a machine came to be, recorded on the environment that got it.
 *
 * A lease used to answer one question — warm or cold — and that stopped being
 * enough the moment provisioning became something reef does rather than
 * something it restores. "Warm" now covers a machine whose dependencies were
 * already installed and a machine that has a source tree and nothing else, and
 * those differ by ninety seconds. So the mode is recorded, and it is the field
 * an engineer reads first when a step that should have started warm did not.
 *
 * - `build` — this boot did the provisioning itself: `.reef/setup.sh` ran here.
 *   The only mode in which a `setup.sh` failure is fatal, because in this mode
 *   setup *is* the machine.
 * - `fresh` — no prepared tree, no image, and no `setup.sh` to run. A clone and
 *   nothing else, which is what every container step got before this existed.
 * - `repo_image` — a prepared root whose dependency fingerprint matches this
 *   checkout's, so setup is skipped and `sync.sh` reconciles the difference.
 * - `snapshot_restore` — a prepared checkout from the warm pool, with no image
 *   behind it. Source restored, dependencies not.
 */
export const BootMode = v.picklist(['build', 'fresh', 'repo_image', 'snapshot_restore']);
export type BootMode = v.InferOutput<typeof BootMode>;

/**
 * The three scripts a repository may keep under `.reef/`.
 *
 * Three rather than one because they fail differently and are therefore fatal
 * differently, which is the whole reason they are separate files: provisioning
 * that failed on a machine that already has an image is a warning, and a
 * service that failed to start is a step working against an application that
 * is not running.
 */
export const LifecycleScript = v.picklist(['setup', 'start', 'sync']);
export type LifecycleScript = v.InferOutput<typeof LifecycleScript>;

/** Where a repository keeps them. One directory, so `ls` answers "is this repo set up for reef". */
export const LIFECYCLE_DIR = '.reef';

/** The path of one script inside a repository, relative to that repository's root. */
export function lifecycleScriptPath(script: LifecycleScript): string {
  return `${LIFECYCLE_DIR}/${script}.sh`;
}

/**
 * How long each script may take, named rather than inline.
 *
 * `setup` is generous because it is a cold dependency install on a monorepo and
 * a ceiling that kills a working install is worse than a slow one — this runs
 * once per fingerprint, not once per step. `start` is deliberately tight: a
 * service that has not answered in three minutes is not "still starting", it is
 * broken, and every second past that is spent by a step that will fail anyway.
 * `sync` sits between them because it is an incremental install of whatever one
 * branch added over another.
 */
export const SETUP_TIMEOUT_MS = 15 * 60_000;
export const START_TIMEOUT_MS = 3 * 60_000;
export const SYNC_TIMEOUT_MS = 5 * 60_000;

/**
 * What one script did.
 *
 * `fatal` is stored rather than derived at read time because the rule depends
 * on the boot mode, and the boot mode is a fact about a moment that has passed
 * — an image built an hour later would make the same exit code look survivable
 * in a report about a step that died of it.
 */
export const LifecycleOutcome = v.object({
  script: LifecycleScript,
  /** Which repository's copy ran. `''` is the workspace root. */
  path: v.optional(v.string(), ''),
  ran: v.boolean(),
  exitCode: v.nullable(v.pipe(v.number(), v.integer())),
  durationMs: v.pipe(v.number(), v.integer()),
  fatal: v.boolean(),
  /** The tail of stderr, bounded. The whole log is the step's own transcript problem. */
  detail: v.optional(v.string(), ''),
});
export type LifecycleOutcome = v.InferOutput<typeof LifecycleOutcome>;

/** Bytes of a script's stderr kept on the outcome. Enough for a stack, not a build log. */
export const LIFECYCLE_DETAIL_CHARS = 2_000;

/**
 * A prepared root, keyed by what it was prepared *for* rather than by a commit.
 *
 * The fingerprint is the key that matters. An image is only reusable while the
 * dependency declarations it was built from still describe the checkout being
 * booted, and a commit sha would be far too strict — every commit would miss,
 * and an image nothing ever hits is a build farm producing nothing but cost.
 *
 * `imageRef` is nullable and that nullability is load-bearing: it is what a
 * container runtime hands back when it can genuinely persist a prepared root.
 * A deployment whose runtime cannot do that records the machine it provisioned
 * on instead, and the image is reusable only *on that machine*. Treating a
 * ref-less image as reusable everywhere would skip `setup.sh` on a machine that
 * has no `node_modules`, which fails later, further away, and in the build.
 */
export const EnvironmentImage = v.object({
  id: v.string(),
  /** `repo` for a `(repo, ref)` pool, `environment` for a named repo set. */
  scope: v.picklist(['repo', 'environment']),
  /** `owner/name@ref` for a repo image, the environment id for an environment image. */
  scopeId: v.string(),
  /** What the fingerprint was taken over: the dependency declarations, hashed. */
  dependencyFingerprint: v.string(),
  commitSha: v.nullable(v.string()),
  state: v.picklist(['building', 'ready', 'failed', 'reaped']),
  /** The runtime's own handle for the prepared root, when the runtime has one. */
  imageRef: v.optional(v.nullable(v.string()), null),
  /** The machine the provisioning ran on, which is the fallback for a null `imageRef`. */
  machineId: v.optional(v.nullable(v.string()), null),
  buildMs: v.optional(v.pipe(v.number(), v.integer()), 0),
  /** Boots served. A zero here after a day is an image whose fingerprint never matches. */
  uses: v.optional(v.pipe(v.number(), v.integer()), 0),
  lastUsedAt: v.optional(v.nullable(v.string()), null),
  readyAt: v.optional(v.nullable(v.string()), null),
  retiredAt: v.optional(v.nullable(v.string()), null),
  retiredReason: v.optional(v.nullable(v.string()), null),
  createdAt: v.string(),
});
export type EnvironmentImage = v.InferOutput<typeof EnvironmentImage>;

/**
 * The dependency declarations of a ref, as of its last push.
 *
 * Stored because the reaper has no tree. It runs on the one-minute sweep, holds
 * no repository contents, and has to answer "is this image built from
 * declarations nobody uses any more" — which is a comparison against the head
 * of the ref, and this row is the only place that number survives the request
 * that computed it.
 */
export const DependencyFingerprint = v.object({
  repo: v.string(),
  ref: v.string(),
  commitSha: v.string(),
  fingerprint: v.string(),
  /** Which files went into it, so a fingerprint that never changes is explicable. */
  manifests: v.array(v.string()),
  updatedAt: v.string(),
});
export type DependencyFingerprint = v.InferOutput<typeof DependencyFingerprint>;

/**
 * Images kept per scope, newest first.
 *
 * Three rather than one, because a deployment mid-rollout has runs on the
 * previous commit and runs on the new one, and an image set of one makes the
 * older half cold-start. Three rather than ten, because every extra image is a
 * prepared root nobody is on — and the pool's own comment says why that is
 * worse than a cold start: it exists, it is offered, and it answers about
 * dependencies that have moved.
 */
export const MAX_IMAGES_PER_SCOPE = 3;

/** An image unused for this long is reaped whatever its fingerprint says. */
export const IMAGE_IDLE_TTL_MINUTES = 7 * 24 * 60;

/**
 * How long a superseded image is kept after its fingerprint stops matching head.
 *
 * Not zero, and the hour is the point: a push lands while runs are in flight
 * against the commit before it, and reaping on the push itself would cold-start
 * every one of them to save an object that is about to be replaced anyway.
 */
export const IMAGE_SUPERSEDED_GRACE_MINUTES = 60;

export const WarmEnvironment = v.object({
  id: v.string(),
  repo: v.string(),
  ref: v.string(),
  /** The commit it was warmed against. What makes staleness a fact rather than a guess. */
  commitSha: v.nullable(v.string()),
  state: EnvironmentState,
  /** The step conversation holding it, when leased. */
  leasedBy: v.optional(v.nullable(v.string()), null),
  leasedAt: v.optional(v.nullable(v.string()), null),
  readyAt: v.optional(v.nullable(v.string()), null),
  retiredAt: v.optional(v.nullable(v.string()), null),
  /** Why it was retired: used, stale, expired, failed. Read on every incident. */
  retiredReason: v.optional(v.nullable(v.string()), null),
  /** R2 key of the prepared workspace, so a lease is a restore rather than a clone. */
  snapshotKey: v.optional(v.nullable(v.string()), null),
  /** Symbols in the index that was built while warming. Zero means the index did not build. */
  indexedSymbols: v.optional(v.pipe(v.number(), v.integer()), 0),
  /** How long warming took. The number the pool exists to move off the critical path. */
  warmMs: v.optional(v.pipe(v.number(), v.integer()), 0),
  /**
   * How this machine came to be, written when it booted rather than when it was
   * warmed.
   *
   * Null until a step boots on it, and that is the honest reading: a row in the
   * pool has a prepared tree and no history yet. The mode is decided against
   * what is true at boot — the image that exists then, the fingerprint of the
   * commit then — so recording it at creation would record a guess.
   */
  bootMode: v.optional(v.nullable(BootMode), null),
  /** The prepared root this boot used, when it used one. */
  imageId: v.optional(v.nullable(v.string()), null),
  /** The dependency fingerprint the boot resolved against, kept for the reaper's comparison. */
  dependencyFingerprint: v.optional(v.nullable(v.string()), null),
  /** What each lifecycle script did on this machine. Empty until it boots. */
  lifecycle: v.optional(v.array(LifecycleOutcome), []),
  createdAt: v.string(),
});
export type WarmEnvironment = v.InferOutput<typeof WarmEnvironment>;

/**
 * A pool, as configured.
 *
 * `size` is the count kept *ready*, not the ceiling on live environments — a
 * pool of four with four leased still warms four more, because the reason to
 * hold four ready is that four steps might start at once and the second one
 * should not pay for a clone either.
 */
export const EnvironmentPool = v.object({
  repo: v.string(),
  ref: v.string(),
  /** Environments kept ready. Zero disables the pool without deleting its history. */
  size: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(50)),
  /**
   * Minutes a ready environment may sit before it is retired as stale.
   *
   * Not a cache eviction: the risk is not memory, it is an agent confidently
   * editing an hour-old checkout of a branch that has moved twice since.
   */
  ttlMinutes: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 60),
  enabled: v.optional(v.boolean(), true),
  updatedAt: v.optional(v.string()),
});
export type EnvironmentPool = v.InferOutput<typeof EnvironmentPool>;

/**
 * What a step got when it asked for an environment.
 *
 * `warm: false` is a normal answer and is returned rather than waited on: a
 * step blocked until the pool refills is a step whose latency is now the pool's
 * worst case instead of its best, which is precisely backwards.
 */
/*
 * There is deliberately no `bootMode` here.
 *
 * A lease is acquired *before* the machine boots — the two are separate moments
 * — so the field would be null at every construction site and filled in at
 * none. How a machine came to be is recorded on `WarmEnvironment`, which is the
 * row that outlives the request, and reported by `BootReport` to the step that
 * has to act on it.
 */
export const EnvironmentLease = v.object({
  environmentId: v.nullable(v.string()),
  warm: v.boolean(),
  repo: v.string(),
  ref: v.string(),
  commitSha: v.nullable(v.string()),
  snapshotKey: v.nullable(v.string()),
  /** Milliseconds the acquisition itself took — not the warming it skipped. */
  acquiredInMs: v.number(),
  /** Said plainly when cold, because the step should mention it rather than look slow. */
  note: v.optional(v.string(), ''),
});
export type EnvironmentLease = v.InferOutput<typeof EnvironmentLease>;

export const EnvironmentPoolStats = v.object({
  repo: v.string(),
  ref: v.string(),
  size: v.pipe(v.number(), v.integer()),
  ready: v.pipe(v.number(), v.integer()),
  leased: v.pipe(v.number(), v.integer()),
  warming: v.pipe(v.number(), v.integer()),
  /** Leases served warm as a share of all leases, over the window. Null before any lease. */
  warmStartShare: v.nullable(v.number()),
  /** Median milliseconds spent warming — what a cold start would have cost. */
  medianWarmMs: v.nullable(v.number()),
  leases: v.pipe(v.number(), v.integer()),
});
export type EnvironmentPoolStats = v.InferOutput<typeof EnvironmentPoolStats>;

/** Ready environments any one pool may hold, whatever its configuration asks for. */
export const MAX_POOL_SIZE = 50;
